import { useLocale } from 'core/hooks/useLocale';
import { getOriginalScale, getOriginalScaleQualitative, isScaleNumeric } from 'core/utils/constants/evaluation';
import { formatDisplayScore } from 'core/utils/formatDisplayScore';
import { parseGradesToScoreScale } from 'core/utils/parses';
import { getUserNoteStatus } from 'core/utils/user';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type ScoreStatus = 'needs-reinforcement' | 'needs-ampliacion' | 'good-progress' | 'empty';

export type EvaluationScale = {
  max?: number;
  min?: number;
  number_decimals?: number;
  ranges?: Array<{ from: number; name: string; to: number }>;
  type?: 'numeric' | 'qualitative';
};

export type ScoreValue = number | string | null;

type UseScoreParams = {
  evaluationScale?: EvaluationScale | null;
  score?: ScoreValue;
  scoreScale?: string;
  toolTipText?: string;
};

type UseScoreResult = {
  displayValue: string;
  shouldShowTooltipMark: boolean;
  status: ScoreStatus;
  statusAnnouncement: string;
};

export const SCORE_STATUS_LABEL_KEYS: Record<ScoreStatus, string> = {
  'good-progress': 'grades:Score status good progress',
  'needs-ampliacion': 'grades:Score status adequate progress',
  'needs-reinforcement': 'grades:Score status needs reinforcement',
  empty: 'grades:Score not evaluated',
};

function hasScoreValue(score?: ScoreValue): boolean {
  return Boolean(score) || score === 0;
}

function useScore({ evaluationScale = null, score, scoreScale, toolTipText }: UseScoreParams): UseScoreResult {
  const locale = useLocale();
  const { t } = useTranslation();

  const parsedScore = useMemo<ScoreValue>(() => {
    if (!hasScoreValue(score)) return null;

    if (!Number.isNaN(Number(score))) {
      if (evaluationScale) {
        return isScaleNumeric(evaluationScale)
          ? getOriginalScale(score, evaluationScale.min, evaluationScale.max, evaluationScale.number_decimals)
          : getOriginalScaleQualitative(evaluationScale, score);
      }
      return parseGradesToScoreScale(Number.parseFloat(String(score)).toFixed(1), scoreScale);
    }

    // Non-numeric values ('-', qualitative names, letter grades) are rendered as-is.
    return score;
  }, [evaluationScale, score, scoreScale]);

  const status = getUserNoteStatus(score) as ScoreStatus;

  const formattedValue = useMemo<string>(() => {
    if (!hasScoreValue(score) || Number.isNaN(Number(parsedScore))) {
      return formatDisplayScore(parsedScore, locale);
    }
    const decimals = isScaleNumeric(evaluationScale) ? evaluationScale?.number_decimals : 1;
    return formatDisplayScore(Number(parsedScore).toFixed(decimals), locale);
  }, [evaluationScale, locale, parsedScore, score]);

  // The asterisk flags a contextual note (turned in late, in progress) explained
  // by the tooltip. Only numeric values carry it, matching the previous behaviour.
  const shouldShowTooltipMark = Boolean(toolTipText) && hasScoreValue(score) && !Number.isNaN(Number(parsedScore));

  // Rendered as visually hidden text next to the value, so the status reaches
  // screen readers without colour being its only carrier (WCAG 1.4.1). The
  // leading separator only makes sense after a visible value.
  const statusLabel = t(SCORE_STATUS_LABEL_KEYS[formattedValue ? status : 'empty']);
  const statusAnnouncement = formattedValue ? `. ${statusLabel}.` : `${statusLabel}.`;

  return {
    displayValue: formattedValue,
    shouldShowTooltipMark,
    status,
    statusAnnouncement,
  };
}

export default useScore;
